import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Ban, CheckCircle2, ShieldCheck, Truck, ScrollText, Star } from "lucide-react";
import { toast } from "sonner";

const UserDetail = () => {
  const { uid } = useParams();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/users/${uid}`);
      setD(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load user");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [uid]);

  const suspend = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/users/${uid}/suspend`, { reason: suspendReason.trim() });
      toast.success("User suspended");
      setSuspendOpen(false);
      setSuspendReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };
  const unsuspend = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/users/${uid}/unsuspend`);
      toast.success("User unsuspended");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !d) return <div className="p-8 text-sm text-slate-500">Loading…</div>;
  const u = d.profile;

  return (
    <div className="p-8">
      <Link to="/admin/users" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-3 h-3"/> Back to users</Link>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-bold text-slate-900" data-testid="user-name">{u.name}</h1>
            <Badge className={`text-[10px] ${u.suspended ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`} data-testid="user-status-badge">
              {u.suspended ? "SUSPENDED" : "ACTIVE"}
            </Badge>
            <Badge className={`text-[10px] ${u.kyc_status === "approved" ? "bg-emerald-100 text-emerald-700" : u.kyc_status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
              KYC {(u.kyc_status || "pending").toUpperCase()}
            </Badge>
          </div>
          <div className="text-sm text-slate-500 mt-1">{u.phone} · {u.email || "no email"}</div>
        </div>
        <div className="flex items-center gap-2">
          {u.suspended
            ? <Button data-testid="btn-unsuspend" onClick={unsuspend} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4 mr-1"/> Unsuspend</Button>
            : <Button data-testid="btn-open-suspend" onClick={() => setSuspendOpen(true)} disabled={busy} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"><Ban className="w-4 h-4 mr-1"/> Suspend</Button>
          }
        </div>
      </div>

      {u.suspended && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 mb-6" data-testid="suspend-banner">
          <div className="text-red-900 font-medium text-sm">Suspended</div>
          <div className="text-xs text-red-800 mt-1"><b>Reason:</b> {u.suspend_reason}</div>
          <div className="text-[11px] text-red-700 mt-0.5">Suspended at {u.suspended_at ? new Date(u.suspended_at).toLocaleString() : ""}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card title="Profile">
          <Row label="Phone" value={u.phone}/>
          <Row label="Email" value={u.email || <em className="text-slate-400">Not collected yet</em>}/>
          <Row label="Signup" value={u.created_at ? new Date(u.created_at).toLocaleString() : "—"}/>
          <Row label="Trust tier" value={(u.trust_tier || "new").toUpperCase()}/>
          <Row label="Referral code" value={u.referral_code}/>
        </Card>
        <Card title="Activity">
          <Row label="Deliveries done" value={u.delivery_count || 0}/>
          <Row label="Rating" value={u.rating_avg ? `${u.rating_avg.toFixed(1)} (${u.rating_count})` : "No ratings yet"}/>
          <Row label="Wallet balance" value={u.wallet_balance ? `₹${u.wallet_balance.toFixed(2)}` : "₹0.00"}/>
          <Row label="Accepts perishables" value={u.accepts_perishables ? "Yes" : "No"}/>
          <Row label="UPI ID" value={u.upi_id || <em className="text-slate-400">Not set</em>}/>
          <Row
            label="Cancellations (as sender)"
            value={<span data-testid="user-cancel-sender-count" className={u.cancellations_as_sender ? "text-rose-700 font-semibold" : ""}>{u.cancellations_as_sender || 0}</span>}
          />
          <Row
            label="Cancellations (as carrier)"
            value={<span data-testid="user-cancel-carrier-count" className={u.cancellations_as_carrier ? "text-rose-700 font-semibold" : ""}>{u.cancellations_as_carrier || 0}</span>}
          />
        </Card>
        <Card title="KYC">
          <Row label="Status" value={(u.kyc_status || "pending").toUpperCase()}/>
          <Row label="Aadhaar (masked)" value={u.aadhaar_number || <em className="text-slate-400">Not collected yet</em>}/>
          <Row label="PAN (masked)" value={u.pan_number || <em className="text-slate-400">Not collected yet</em>}/>
          {u.kyc_status === "rejected" && (
            <>
              <Row label="Reject reason" value={u.kyc_rejection_reason}/>
              <Row label="Reject note" value={u.kyc_rejection_note}/>
            </>
          )}
          {u.kyc_doc_id && <Link to={`/admin/kyc/${u.id}`} className="text-xs text-slate-900 underline mt-2 inline-block">Open KYC review →</Link>}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card title={<>Bank details {u.bank_verified ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 ml-1 font-semibold">VERIFIED</span> : <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 ml-1 font-semibold">UNVERIFIED</span>}</>}>
          <Row label="Account holder" value={u.account_holder_name || <em className="text-slate-400">Not collected yet</em>}/>
          <Row label="Account number" value={u.account_number ? <span className="font-mono">{u.account_number}</span> : <em className="text-slate-400">Not collected yet</em>}/>
          <Row label="IFSC code" value={u.ifsc_code ? <span className="font-mono">{u.ifsc_code}</span> : <em className="text-slate-400">Not collected yet</em>}/>
          <Row label="UPI ID" value={u.upi_id || <em className="text-slate-400">Not set</em>}/>
          {u.bank_verified && u.bank_verified_at && <Row label="Verified at" value={new Date(u.bank_verified_at).toLocaleString()}/>}
          {u.bank_verified_note && <Row label="Verification note" value={u.bank_verified_note}/>}
          {(u.account_holder_name && u.account_number && u.ifsc_code) && (
            <div className="mt-3 flex gap-2">
              {!u.bank_verified ? (
                <button
                  data-testid="verify-bank-btn"
                  onClick={async () => {
                    const note = prompt("Verification note (e.g. 'penny-drop OK', 'checked by phone')?") || "";
                    try {
                      await adminApi.post(`/users/${u.id}/verify-bank`, { verified: true, note });
                      window.location.reload();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  }}
                  className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-full font-semibold"
                >Mark bank verified</button>
              ) : (
                <button
                  data-testid="unverify-bank-btn"
                  onClick={async () => {
                    if (!window.confirm("Mark bank details as unverified? The carrier will be excluded from payout exports until re-verified.")) return;
                    try {
                      await adminApi.post(`/users/${u.id}/verify-bank`, { verified: false, note: "" });
                      window.location.reload();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  }}
                  className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-full font-semibold"
                >Revoke verification</button>
              )}
            </div>
          )}
        </Card>
        <Card title={<><Truck className="w-4 h-4 inline mr-1"/> Vehicles ({(u.vehicles || []).length})</>}>
          <Vehicles list={u.vehicles || []}/>
        </Card>
        <Card title={<><ShieldCheck className="w-4 h-4 inline mr-1"/> Legal acceptances</>}>
          <table className="w-full text-xs">
            <thead className="text-slate-500"><tr><th className="text-left">Document</th><th>Ver</th><th className="text-right">Accepted</th></tr></thead>
            <tbody>
              {d.legal_acceptances.map((a, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-1.5 capitalize">{a.doc.replace("_", " ")}</td>
                  <td className="py-1.5 text-center tabular-nums">v{a.version}</td>
                  <td className="py-1.5 text-right text-slate-500">{new Date(a.accepted_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {d.legal_acceptances.length === 0 && <tr><td colSpan={3} className="text-center py-3 text-slate-400 italic">None accepted yet</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card title={<><ScrollText className="w-4 h-4 inline mr-1"/> Deliveries as sender ({d.deliveries_as_sender.length})</>}>
          <DeliveryList list={d.deliveries_as_sender}/>
        </Card>
        <Card title={<><ScrollText className="w-4 h-4 inline mr-1"/> Deliveries as carrier ({d.deliveries_as_carrier.length})</>}>
          <DeliveryList list={d.deliveries_as_carrier}/>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title={<><Star className="w-4 h-4 inline mr-1"/> Ratings received ({d.ratings_received.length})</>}>
          {d.ratings_received.length === 0 && <div className="text-xs text-slate-400 italic">No ratings yet.</div>}
          {d.ratings_received.slice(0, 8).map((r, i) => (
            <div key={i} className="text-xs py-1.5 border-t border-slate-100 first:border-t-0">
              <span className="font-medium">{r.stars}★</span> <span className="text-slate-500">— {r.comment || "no comment"}</span>
            </div>
          ))}
        </Card>
        <Card title="Disputes involving this user">
          {d.disputes.length === 0 && <div className="text-xs text-slate-400 italic">No disputes.</div>}
          {d.disputes.map((s, i) => (
            <div key={i} className="text-xs py-1.5 border-t border-slate-100 first:border-t-0">
              <span className="font-medium">{s.status}</span> — {s.description || "no description"}
            </div>
          ))}
        </Card>
      </div>

      <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend {u.name}?</DialogTitle>
            <DialogDescription>They won't be able to log in or use the app until unsuspended. This is recorded in the audit log.</DialogDescription>
          </DialogHeader>
          <label className="text-xs font-medium text-slate-500">Reason (required, shown to the user)</label>
          <Textarea data-testid="suspend-reason" value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} rows={3} placeholder="e.g. KYC documents appear forged"/>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setSuspendOpen(false)}>Cancel</Button>
            <Button data-testid="btn-confirm-suspend" disabled={!suspendReason.trim() || busy} onClick={suspend} className="bg-red-600 hover:bg-red-700">Suspend</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Card = ({ title, children }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-4">
    <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-medium">{title}</div>
    <div className="space-y-1">{children}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-1 text-sm gap-2">
    <span className="text-slate-500 text-xs">{label}</span>
    <span className="text-slate-900 text-right">{value ?? <em className="text-slate-400">—</em>}</span>
  </div>
);

const Vehicles = ({ list }) => {
  if (!list.length) return <div className="text-xs text-slate-400 italic">No vehicles.</div>;
  return list.map((v, i) => (
    <div key={i} className="text-xs py-1.5 border-t border-slate-100 first:border-t-0 flex items-center justify-between gap-2">
      <div>
        <span className="font-medium uppercase">{v.type}</span>
        {v.make_model && <span className="text-slate-500"> · {v.make_model}</span>}
        {v.reg_no && <span className="text-slate-500"> · {v.reg_no}</span>}
      </div>
      {["car", "bike", "suv"].includes(v.type) && (
        <span className={v.insurance_current ? "text-emerald-700 text-[10px]" : "text-slate-400 text-[10px]"}>
          {v.insurance_current ? "Insurance current" : "Insurance not confirmed"}
        </span>
      )}
    </div>
  ));
};

const DeliveryList = ({ list }) => {
  if (!list.length) return <div className="text-xs text-slate-400 italic">None</div>;
  return list.slice(0, 8).map((d, i) => (
    <Link key={i} to={`/admin/parcels/${d.parcel_id}`} className="block text-xs py-1.5 border-t border-slate-100 first:border-t-0 hover:bg-slate-50 -mx-2 px-2 rounded">
      <span className="font-mono">{d.parcel_id.slice(0, 8)}</span>
      <span className="text-slate-500"> · {d.status}</span>
      <span className="text-slate-400"> · ₹{d.sender_total?.toFixed(0) || "—"}</span>
    </Link>
  ));
};

export default UserDetail;
